/* eslint-disable indent,react/jsx-indent */
import * as React from 'react';
import { lazy, Suspense, useEffect } from 'react';
import { runInAction } from 'mobx';
import { store } from 'Models/Store';
import { Route, Routes, Navigate } from 'react-router-dom';
import * as Pages from './Pages';
import { LegacySearchResultsRedirect, LegacyStaffAccountDetailsRedirect } from './Pages/LegacyUserRedirects';
import Logout from './Components/Logout/Logout';
import Auth from './Components/Auth/Auth';
import { PageNotFound } from './Pages/PageNotFound';
import { PageAccessDenied } from './Pages/PageAccessDenied';
import Topbar from './Components/Topbar/Topbar';
import ErrorBoundary from './ErrorBoundary';
import { InvalidToken } from './Components/ErrorHandling/InvalidToken';
import { SomethingWentWrong } from './Components/ErrorHandling/SomethingWentWrong';
import { LottieSpinner } from './Components/_HumanWritten/Lottie/LottieSpinner';
import { ROUTES } from 'Constants';
import { observer } from 'mobx-react';
import { useBookingWizardNavigationGuard } from 'Hooks/useBookingWizardNavigationGuard';

// Pages that are large and/or rarely hit are split out of the main bundle and loaded on
// demand. Everything the typical customer touches (home, schedule, booking wizard, bookings,
// account, success pages) stays eager via the Pages barrel, so common navigation shows no
// loading spinner.

// Standalone lazy chunks — big and infrequently visited.
const AdminPage = lazy(() => import('./Pages/FrontendAdmin/AdminPage'));
const FerryCheckInPage = lazy(() => import('./Pages/FerryCheckInPage'));
const EventCheckInPage = lazy(() => import('./Pages/EventCheckInPage'));
const ComponentLibraryPage = lazy(() => import('./Pages/ComponentLibraryPage'));
const BookingParkingDetailPage = lazy(() => import('./Pages/BookingParkingDetailPage'));
const CustomerCharterPage = lazy(() => import('./Pages/CustomerCharterPage'));
const ParkingReservationDetailPage = lazy(() => import('./Pages/ParkingReservationDetailPage'));

// Staff back-office cluster — staff-only, so grouped into one chunk (via StaffPagesBundle).
// The whole cluster downloads on first visit to any staff page; sibling navigation is instant.
const staffBundle = () => import('./Pages/_bundles/StaffPagesBundle');
const StaffBookingsPage = lazy(() => staffBundle().then(m => ({ default: m.StaffBookingsPage })));
const StaffTransactionsPage = lazy(() => staffBundle().then(m => ({ default: m.StaffTransactionsPage })));
const StaffFeesPage = lazy(() => staffBundle().then(m => ({ default: m.StaffFeesPage })));
const StaffFerryTripsPage = lazy(() => staffBundle().then(m => ({ default: m.StaffFerryTripsPage })));
const StaffSchedulePage = lazy(() => staffBundle().then(m => ({ default: m.StaffSchedulePage })));
const StaffVouchersPage = lazy(() => staffBundle().then(m => ({ default: m.StaffVouchersPage })));
const StaffRefundsPage = lazy(() => staffBundle().then(m => ({ default: m.StaffRefundsPage })));
const StaffUserEntitlementsPage = lazy(() => staffBundle().then(m => ({ default: m.StaffUserEntitlementsPage })));
const UsersPage = lazy(() => staffBundle().then(m => ({ default: m.UsersPage })));

// Events cluster — gated behind the `eventsEnabled` feature flag, so operators without events
// never download this chunk. Grouped so events navigation stays within one chunk.
const eventBundle = () => import('./Pages/_bundles/EventPagesBundle');
const EventListView = lazy(() => eventBundle().then(m => ({ default: m.EventListView })));
const EventsPage = lazy(() => eventBundle().then(m => ({ default: m.EventsPage })));
const EventBookingWizardPage = lazy(() => eventBundle().then(m => ({ default: m.EventBookingWizardPage })));
const EventBookingSuccessPage = lazy(() => eventBundle().then(m => ({ default: m.EventBookingSuccessPage })));

const Frontend = observer(() => {
	useEffect(() => {
		runInAction(() => {
			store.appLocation = 'frontend';
		});
	}, []);

	// Global navigation guard for booking wizard alterations
	useBookingWizardNavigationGuard();

	return (
		<>
			<div className="body-container">
				<Topbar currentLocation="frontend" />
				<div className="frontend">
					<ErrorBoundary>
						<Suspense fallback={<LottieSpinner />}>
							<Routes>
								{/* Public routes */}
								<Route path="/login/*" element={<Pages.LoginRoutes />} />
								<Route path="/logout" element={<Logout />} />
								<Route path="/register" element={<Pages.RegisterPage />} />
								<Route path={ROUTES.CONVERT_GUEST} element={<Pages.ConvertGuestPage />} />
								<Route path="/confirm-email" element={<Pages.RegistrationConfirmPage />} />
								<Route path="/reset-password-request" element={<Pages.ResetPasswordRequestPage />} />
								<Route path="/reset-password" element={<Pages.ResetPasswordPage />} />
								<Route path="/home" element={<Pages.HomePage />} />
								<Route path="/home/search" element={<Pages.HomeSearchPage />} />
								<Route path="/" element={<Navigate to="/home" replace />} />
								<Route path="/booking-wizard/*" element={<Pages.BookingWizardPage />} />
								<Route path="/event-booking-wizard/*" element={<EventBookingWizardPage />} />
								<Route path={`${ROUTES.FERRY_SCHEDULE}/*`} element={<Pages.FerrySchedulePage />} />
								<Route path="/parking" element={<Pages.ParkingPage />} />
								<Route path="/remove-from-waitlist" element={<Pages.RemoveFromWaitlistPage />} />
								<Route path="/upcoming-events/*" element={<EventListView />} />
								<Route path="/event-details/:id?" element={<EventsPage />} />
								<Route path="/404" element={<PageNotFound />} />
								<Route path="/403" element={<PageAccessDenied />} />
								<Route path="/invalid-token" element={<InvalidToken />} />
								<Route path="/whoops" element={<SomethingWentWrong error={null} />} />
								{/* Protected routes wrapped in Auth */}
								<Route
									path="/admin/*"
									element={
										<Auth>
											<AdminPage />
										</Auth>
									}
								/>
								<Route
									path="/account-details/:tab?"
									element={
										<Auth>
											<Pages.AccountDetailsPage />
										</Auth>
									}
								/>
								<Route
									path="/staff-account-details/:id/:tab?"
									element={<LegacyStaffAccountDetailsRedirect />}
								/>
								<Route
									path={ROUTES.USER_DETAIL.path}
									element={
										<Auth>
											<Pages.StaffUserDetailPage />
										</Auth>
									}
								/>
								<Route
									path="/staff-bookings"
									element={
										<Auth>
											<StaffBookingsPage />
										</Auth>
									}
								/>
								<Route
									path="/bookings/:id/parking"
									element={
										<Auth>
											<BookingParkingDetailPage />
										</Auth>
									}
								/>
								<Route
									path={ROUTES.CUSTOMER_CHARTER.path}
									element={
										<Auth>
											<CustomerCharterPage />
										</Auth>
									}
								/>
								<Route
									path="/bookings/:id"
									element={
										<Auth>
											<Pages.BookingDetailPage />
										</Auth>
									}
								/>
								<Route
									path="/bookings"
									element={
										<Auth>
											<Pages.BookingsPage />
										</Auth>
									}
								/>
								<Route
									path="/update-password"
									element={
										<Auth>
											<Pages.UpdatePasswordPage />
										</Auth>
									}
								/>
								<Route
									path={ROUTES.REGISTER_CUSTOMER}
									element={
										<Auth>
											<Pages.RegisterCustomerPage />
										</Auth>
									}
								/>
								<Route
									path="/booking-success"
									element={
										<Auth>
											<Pages.BookingSuccessPage />
										</Auth>
									}
								/>
								<Route
									path="/parking/success"
									element={
										<Auth>
											<Pages.ParkingSuccessPage />
										</Auth>
									}
								/>
								<Route
									path="/event-booking-success"
									element={
										<Auth>
											<EventBookingSuccessPage />
										</Auth>
									}
								/>
								<Route
									path={ROUTES.BOOKING_CANCELLED_SUCCESS.path}
									element={
										<Auth>
											<Pages.BookingCancelSuccessPage />
										</Auth>
									}
								/>
								<Route
									path={ROUTES.CHECKIN}
									element={
										<Auth>
											<FerryCheckInPage />
										</Auth>
									}
								/>
								<Route
									path="/event-check-in/:id/*"
									element={
										<Auth>
											<EventCheckInPage />
										</Auth>
									}
								/>
								<Route
									path="/qr-code/booking/:id"
									element={
										<Auth>
											<Pages.QrCodeRedirectPage />
										</Auth>
									}
								/>
								<Route
									path="/notification/:notificationId"
									element={
										<Auth>
											<Pages.NotificationRedirectPage />
										</Auth>
									}
								/>
								<Route path={ROUTES.SEARCH_RESULTS.path} element={<LegacySearchResultsRedirect />} />
								<Route
									path="/change-email"
									element={
										<Auth>
											<Pages.ChangeEmailPage />
										</Auth>
									}
								/>
								<Route
									path="/awaiting-payment"
									element={
										<Auth>
											<Pages.AwaitEwayPaymentPage />
										</Auth>
									}
								/>
								<Route
									path="/claim-voucher"
									element={
										<Auth>
											<Pages.ClaimVoucherPage />
										</Auth>
									}
								/>

								<Route
									path="/transactions"
									element={
										<Auth>
											<StaffTransactionsPage />
										</Auth>
									}
								/>
								<Route
									path="/fees"
									element={
										<Auth>
											<StaffFeesPage />
										</Auth>
									}
								/>
								<Route
									path={`${ROUTES.FERRY_TRIPS}/*`}
									element={
										<Auth>
											<StaffFerryTripsPage />
										</Auth>
									}
								/>
								<Route
									path={`${ROUTES.STAFF_SCHEDULE}/*`}
									element={
										<Auth>
											<StaffSchedulePage />
										</Auth>
									}
								/>
								<Route
									path="/vouchers"
									element={
										<Auth>
											<StaffVouchersPage />
										</Auth>
									}
								/>
								<Route
									path="/refunds"
									element={
										<Auth>
											<StaffRefundsPage />
										</Auth>
									}
								/>
								<Route
									path="/anytime-tickets"
									element={
										<Auth>
											<StaffUserEntitlementsPage />
										</Auth>
									}
								/>
								<Route
									path="/parking-reservations/:id"
									element={
										<Auth>
											<ParkingReservationDetailPage />
										</Auth>
									}
								/>
								<Route
									path="/qr-code/parking/:id"
									element={
										<Auth>
											<ParkingReservationDetailPage />
										</Auth>
									}
								/>
								<Route
									path={ROUTES.USERS}
									element={
										<Auth>
											<UsersPage />
										</Auth>
									}
								/>
								<Route
									path="/component-library"
									element={
										<Auth>
											<ComponentLibraryPage />
										</Auth>
									}
								/>

								{/* Catch-all route for 404 */}
								<Route path="*" element={<PageNotFound />} />
							</Routes>
						</Suspense>
					</ErrorBoundary>
				</div>
			</div>
		</>
	);
});

export default Frontend;
